import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setMenu } from "../../store/feature/commonSlice";
import { getSidebar } from "../../utility/Sidebarmenu";
import {
  useGetCustomerServicesQuery,
  useDataEnableServiceQuery,
} from "../../store/services/flowApi";
import { useGetCustomerLogDeleteKeyQuery } from "../../store/services/reportApi";
import { useGetProfileDetailsQuery } from "../../store/services/userApi";
import MenuOutlined from "@mui/icons-material/MenuOutlined";
import CloseIcon from "@mui/icons-material/Close";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import EastIcon from "@mui/icons-material/East";
import { Box, List, ListItem, ListItemIcon } from "@mui/material";

// Inject keyframe animations once
if (
  typeof document !== "undefined" &&
  !document.getElementById("sidebar-glow-keyframes")
) {
  const style = document.createElement("style");
  style.id = "sidebar-glow-keyframes";
  style.innerHTML = `
    @keyframes pulseSidebarGlow1 {
      0%,100% { opacity:.7; transform:scale(1); }
      50% { opacity:1; transform:scale(1.1); }
    }
    @keyframes pulseSidebarGlow2 {
      0%,100% { opacity:.5; transform:scale(1); }
      50% { opacity:.9; transform:scale(1.12); }
    }
  `;
  document.head.appendChild(style);
}

// Soft pastel colors — one per nav item slot, cycles if more items
const ICON_COLORS = [
  { idle: "#86efac", active: "#4ade80" }, // green
  { idle: "#93c5fd", active: "#60a5fa" }, // blue
  { idle: "#f9a8d4", active: "#f472b6" }, // pink
  { idle: "#fcd34d", active: "#fbbf24" }, // amber
  { idle: "#a5b4fc", active: "#818cf8" }, // indigo
  { idle: "#6ee7b7", active: "#34d399" }, // emerald
  { idle: "#fdba74", active: "#fb923c" }, // orange
  { idle: "#c4b5fd", active: "#a78bfa" }, // violet
  { idle: "#67e8f9", active: "#22d3ee" }, // cyan
  { idle: "#fda4af", active: "#fb7185" }, // rose
];

const getColor = (index) => ICON_COLORS[index % ICON_COLORS.length];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const common = useSelector((state) => state.common);
  const reseller = localStorage.getItem("reseller");

  const { data: apiData, isLoading, isError } = useGetCustomerServicesQuery();
  const { data: featureKeyData } = useGetCustomerLogDeleteKeyQuery();
  const canShowTransaction = featureKeyData?.data?.show_transaction === true;
  const canShowWorkflow = featureKeyData?.data?.is_workflow_given === true;
  const { data: manageDbData } = useDataEnableServiceQuery();
  const { data: profileData } = useGetProfileDetailsQuery();

  const isDataEnabled =
    manageDbData?.isdataenable === true &&
    Number(manageDbData?.customer_data_rates ?? 0) > 0;
  const customerId = profileData?.data?.customer_id;

  const [filterSidebar, setFiltersidebar] = useState([]);
  const [getIndex, setGetIndex] = useState(null);
  const [openChild, setOpenChild] = useState({});
  const [currentHostname, setCurrentHostname] = useState("");

  useEffect(() => {
    setCurrentHostname(window.location.hostname);
  }, []);

  useEffect(() => {
    const services = apiData?.data || [];
    const sidebarData = getSidebar(
      services,
      isDataEnabled,
      canShowTransaction,
      customerId,
      canShowWorkflow,
      reseller === "true",
    );
    const filteredSidebar =
      reseller === "true"
        ? sidebarData
        : sidebarData.filter((item) => item.title !== "Reseller");
    setFiltersidebar(filteredSidebar);
  }, [
    apiData,
    isDataEnabled,
    reseller,
    canShowTransaction,
    customerId,
    canShowWorkflow,
  ]);

  const handleExpand = (index) =>
    setGetIndex((prev) => (prev === index ? null : index));
  const handleNavigate = (path) => {
    setGetIndex(null);
    if (path) navigate(path);
  };
  const handleNavigatechild = (path) => {
    if (path) navigate(path);
  };
  const handleChildToggle = (parentIndex, childIndex) => {
    setOpenChild((prev) => ({
      ...prev,
      [parentIndex]: prev[parentIndex] === childIndex ? null : childIndex,
    }));
  };
  const handleToggle = () => dispatch(setMenu(!common.menu));

  if (isLoading)
    return (
      <Box sx={{ p: 2, color: "rgba(255,255,255,0.5)", height: "100vh" }}>
        Loading...
      </Box>
    );
  if (isError)
    return (
      <Box sx={{ p: 2, color: "#f87171", height: "100vh" }}>
        Error loading sidebar
      </Box>
    );

  return (
    <Box sx={styles.sidebar}>
      <Box sx={styles.glowTop} />
      <Box sx={styles.glowBottom} />

      {/* ── Header ── */}
      <Box sx={styles.header}>
        {/* Toggle button — always visible */}
        <Box onClick={handleToggle} sx={styles.menuBtn}>
          {common.menu ? (
            <MenuOutlined
              sx={{ fontSize: 20, color: "rgba(255,255,255,0.75)" }}
            />
          ) : (
            <CloseIcon sx={{ fontSize: 20, color: "rgba(255,255,255,0.75)" }} />
          )}
        </Box>

        {/* Logo — centered between toggle btn and right spacer */}
        {!common.menu && (
          <a
            href="/"
            style={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textDecoration: "none",
            }}
          >
            <img
              src="/logonew_white_text-removebg-preview.png"
              alt="One Space Logo"
              style={{
                height: "52px",
                width: "auto",
                maxWidth: "160px",
                objectFit: "contain",
                filter: "brightness(1.1)",
                cursor: "pointer",
                display: "block",
              }}
            />
          </a>
        )}

        {/* Right spacer — mirrors menu btn width so logo stays truly centered */}
        {!common.menu && <Box sx={{ width: 34, flexShrink: 0 }} />}
      </Box>

      {/* ── Nav Menu ── */}
      <Box sx={styles.navMenu}>
        {filterSidebar.map((item, index) => {
          const color = getColor(index);
          const isTopActive =
            location.pathname === item.path ||
            item.submenu?.some((s) => s.path === location.pathname) ||
            item.submenu?.some((s) =>
              s.submenu?.some((d) => d.path === location.pathname),
            );
          const isExpanded = getIndex === index;

          return (
            <Box key={index} sx={{ mb: 0.5 }}>
              {/* TOP LEVEL */}
              <Box
                onClick={() =>
                  item?.submenu?.length > 0
                    ? handleExpand(index)
                    : handleNavigate(item.path)
                }
                sx={{
                  ...styles.navItem,
                  ...(isTopActive ? styles.navItemActive : {}),
                }}
              >
                {isTopActive && <Box sx={styles.activeBar} />}

                <Box sx={styles.navItemInner}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {/* Icon */}
                    <Box
                      sx={{
                        display: "flex",
                        color: isTopActive ? color.active : color.idle,
                        fontSize: 18,
                        transition: "color 0.25s",
                      }}
                    >
                      {item.icon}
                    </Box>

                    {!common.menu && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: "13.5px",
                          fontWeight: isTopActive ? 500 : 400,
                          color: isTopActive ? "#fff" : "rgba(255,255,255,0.6)",
                          transition: "color 0.25s",
                        }}
                      >
                        {item.title}
                      </Box>
                    )}
                  </Box>

                  {!common.menu && item.submenu?.length > 0 && (
                    <NavigateNextIcon
                      sx={{
                        fontSize: 17,
                        color: "rgba(255,255,255,0.3)",
                        transition: "transform 0.3s ease",
                        transform: isExpanded
                          ? "rotate(90deg)"
                          : "rotate(0deg)",
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* SECOND LEVEL */}
              <Box
                sx={{
                  maxHeight: isExpanded ? 500 : 0,
                  overflow: "hidden",
                  transition: "max-height 0.3s ease",
                }}
              >
                <List sx={{ py: 0.5, pl: common.menu ? 0 : 1 }}>
                  {item.submenu?.map((itm, i) => {
                    const childColor = getColor(index);
                    const hasSubmenu = itm.submenu?.length > 0;
                    const isChildOpen = openChild[index] === i;
                    const isChildActive =
                      location.pathname === itm.path ||
                      itm.submenu?.some((d) => d.path === location.pathname);

                    return (
                      <Box key={i}>
                        <ListItem
                          onClick={() =>
                            hasSubmenu
                              ? handleChildToggle(index, i)
                              : handleNavigatechild(itm.path)
                          }
                          sx={{
                            ...styles.subItem,
                            ...(isChildActive ? styles.subItemActive : {}),
                          }}
                        >
                          <Box sx={styles.navItemInner}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <ListItemIcon
                                sx={{
                                  minWidth: 28,
                                  color: isChildActive
                                    ? childColor.active
                                    : childColor.idle,
                                  transition: "color 0.25s",
                                }}
                              >
                                {itm.icon ? (
                                  itm.icon
                                ) : (
                                  <EastIcon sx={{ fontSize: 13 }} />
                                )}
                              </ListItemIcon>
                              {!common.menu && (
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: "13px",
                                    fontWeight: isChildActive ? 500 : 400,
                                    color: isChildActive
                                      ? "#fff"
                                      : "rgba(255,255,255,0.55)",
                                    transition: "color 0.25s",
                                  }}
                                >
                                  {itm.title}
                                </Box>
                              )}
                            </Box>
                            {hasSubmenu && !common.menu && (
                              <NavigateNextIcon
                                sx={{
                                  fontSize: 15,
                                  color: "rgba(255,255,255,0.3)",
                                  transition: "transform 0.3s ease",
                                  transform: isChildOpen
                                    ? "rotate(90deg)"
                                    : "rotate(0deg)",
                                }}
                              />
                            )}
                          </Box>
                        </ListItem>

                        {/* THIRD LEVEL */}
                        {hasSubmenu && (
                          <Box
                            sx={{
                              maxHeight: isChildOpen ? 400 : 0,
                              overflow: "hidden",
                              transition: "max-height 0.25s ease",
                              pl: 2,
                            }}
                          >
                            <List sx={{ py: 0.3 }}>
                              {itm.submenu.map((sub, j) => {
                                const deepColor = getColor(index);
                                const isDeepActive =
                                  location.pathname === sub.path;
                                return (
                                  <ListItem
                                    key={j}
                                    onClick={() =>
                                      handleNavigatechild(sub.path)
                                    }
                                    sx={{
                                      ...styles.deepItem,
                                      ...(isDeepActive
                                        ? styles.deepItemActive
                                        : {}),
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <ListItemIcon
                                        sx={{
                                          minWidth: 20,
                                          color: isDeepActive
                                            ? deepColor.active
                                            : deepColor.idle,
                                          transition: "color 0.25s",
                                        }}
                                      >
                                        {sub.icon ? (
                                          sub.icon
                                        ) : (
                                          <EastIcon sx={{ fontSize: 11 }} />
                                        )}
                                      </ListItemIcon>
                                      {!common.menu && (
                                        <Box
                                          component="span"
                                          sx={{
                                            fontSize: "12px",
                                            fontWeight: isDeepActive
                                              ? 500
                                              : 400,
                                            color: isDeepActive
                                              ? "#fff"
                                              : "rgba(255,255,255,0.5)",
                                            transition: "color 0.25s",
                                          }}
                                        >
                                          {sub.title}
                                        </Box>
                                      )}
                                    </Box>
                                  </ListItem>
                                );
                              })}
                            </List>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </List>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ─── Style tokens ─────────────────────────────────────────────────────────────

const styles = {
  sidebar: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    position: "relative",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background:
      "linear-gradient(160deg, rgba(15,30,53,0.5) 0%, rgba(10,22,40,0.5) 50%, rgba(8,13,26,0.5) 100%)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    borderRight: "1px solid rgba(99,130,255,0.12)",
    overflow: "hidden",
    transformStyle: "preserve-3d",
    perspective: "1000px",
    flexShrink: 0,
  },
  glowTop: {
    position: "absolute",
    top: -80,
    left: -60,
    width: 260,
    height: 260,
    background:
      "radial-gradient(circle, rgba(59,130,246,0.25) 0%, transparent 70%)",
    pointerEvents: "none",
    zIndex: 0,
    animation: "pulseSidebarGlow1 4s ease-in-out infinite",
  },
  glowBottom: {
    position: "absolute",
    bottom: 60,
    right: -60,
    width: 220,
    height: 220,
    background:
      "radial-gradient(circle, rgba(139,92,246,0.2) 0%, transparent 70%)",
    pointerEvents: "none",
    zIndex: 0,
    animation: "pulseSidebarGlow2 5s ease-in-out infinite",
  },
  header: {
    height: 80, // taller for logo breathing room
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between", // [btn] [logo centered] [spacer]
    px: 2,
    borderBottom: "1px solid rgba(255,255,255,0.07)",
    flexShrink: 0,
    position: "relative",
    zIndex: 2,
  },
  menuBtn: {
    width: 34,
    height: 34,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background 0.2s",
    "&:hover": { background: "rgba(255,255,255,0.06)" },
  },
  navMenu: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    py: 1.5,
    px: 1.5,
    position: "relative",
    zIndex: 2,
    "&::-webkit-scrollbar": { width: "3px" },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(255,255,255,0.1)",
      borderRadius: "3px",
    },
  },
  navItem: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    px: 1.5,
    py: 1.1,
    borderRadius: "10px",
    cursor: "pointer",
    border: "1px solid transparent",
    transition: "all 0.25s ease",
    "&:hover": {
      background: "rgba(99,130,255,0.07)",
      borderColor: "rgba(99,130,255,0.15)",
      transform: "translateX(2px)",
    },
  },
  navItemActive: {
    background:
      "linear-gradient(135deg, rgba(59,130,246,0.18) 0%, rgba(99,102,241,0.12) 100%)",
    borderColor: "rgba(99,130,246,0.3)",
    transform: "translateX(3px)",
    boxShadow:
      "0 2px 12px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.06)",
  },
  activeBar: {
    position: "absolute",
    left: "-1px",
    top: "50%",
    transform: "translateY(-50%)",
    width: "3px",
    height: "65%",
    background: "linear-gradient(to bottom, #60a5fa, #a78bfa)",
    borderRadius: "0 3px 3px 0",
  },
  navItemInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  subItem: {
    py: 0.8,
    pl: 1.5,
    pr: 1,
    mb: 0.3,
    borderRadius: "8px",
    cursor: "pointer",
    border: "1px solid transparent",
    transition: "all 0.2s ease",
    "&:hover": { background: "rgba(255,255,255,0.04)" },
  },
  subItemActive: {
    background: "rgba(59,130,246,0.08)",
    borderColor: "rgba(99,102,241,0.15)",
  },
  deepItem: {
    py: 0.6,
    pl: 1,
    pr: 1,
    mb: 0.3,
    borderRadius: "7px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": { background: "rgba(255,255,255,0.03)" },
  },
  deepItemActive: {
    background: "rgba(59,130,246,0.07)",
  },
};

export default Sidebar;
